import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import playerService from '../../services/playerService';
import reportService from '../../services/reportService';

const CommandPalette = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { logout, isAdmin } = useAuth();
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentPlayers, setRecentPlayers] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Load recent players
  useEffect(() => {
    const loadRecent = async () => {
      try {
        const players = await playerService.getAllPlayers();
        setRecentPlayers(players.slice(0, 3));
      } catch (e) {
        console.error('Failed to load recent players:', e);
      }
    };
    if (isOpen) {
      loadRecent();
    }
  }, [isOpen]);

  // Search as user types
  useEffect(() => {
    const search = async () => {
      if (!query.trim()) {
        setSearchResults([]);
        return;
      }

      setLoading(true);
      try {
        const [players, reports] = await Promise.all([
          playerService.getAllPlayers({ search: query }),
          reportService.getAllReports()
        ]);

        const results = [];
        
        // Add matching players
        players.slice(0, 5).forEach(player => {
          results.push({
            type: 'player',
            icon: '⚽',
            title: player.name,
            subtitle: `${player.position} · ${player.currentClub}`,
            action: () => navigate(`/players/${player._id}`)
          });
        });

        // Add matching reports
        reports
          .filter(r => r.player?.name?.toLowerCase().includes(query.toLowerCase()))
          .slice(0, 3)
          .forEach(report => {
            results.push({
              type: 'report',
              icon: '📋',
              title: `Report: ${report.player?.name}`,
              subtitle: `Rating: ${report.overallRating}/10 · ${report.status}`,
              action: () => navigate(`/reports/${report._id}`)
            });
          });

        setSearchResults(results);
      } catch (e) {
        console.error('Search failed:', e);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(search, 200);
    return () => clearTimeout(debounce);
  }, [query, navigate]);

  // Quick actions - wrapped in useMemo to avoid dependency issues
  const quickActions = useMemo(() => {
    const actions = [
      { icon: '➕', title: 'Add new player', shortcut: '⌘N', action: () => navigate('/players/new') },
      { icon: '📋', title: 'Create scout report', shortcut: '⌘⇧R', action: () => navigate('/reports/new') },
      { icon: '📊', title: 'Go to Dashboard', shortcut: '⌘1', action: () => navigate('/dashboard') },
      { icon: '⚽', title: 'Go to Players', shortcut: '⌘2', action: () => navigate('/players') },
      { icon: '📋', title: 'Go to Reports', shortcut: '⌘3', action: () => navigate('/reports') },
    ];

    if (isAdmin) {
      actions.push({ 
        icon: '🏢', 
        title: 'Go to Organization', 
        shortcut: '⌘4', 
        action: () => navigate('/organization') 
      });
    }

    actions.push(
      { icon: '⚙️', title: 'Settings', shortcut: '⌘,', action: () => navigate('/settings') },
      { icon: '🚪', title: 'Logout', shortcut: '', action: () => { logout(); navigate('/login'); } }
    );

    return actions;
  }, [isAdmin, logout, navigate]);

  // Build all items
  const getAllItems = useCallback(() => {
    const items = [];

    if (query.trim()) {
      // Show search results
      if (loading) {
        return [{ type: 'loading', title: 'Searching...' }];
      }
      if (searchResults.length > 0) {
        return searchResults;
      }
      return [{ type: 'empty', title: 'No results found' }];
    }

    // Show default view
    if (recentPlayers.length > 0) {
      items.push({ type: 'section', title: 'Recent Players' });
      recentPlayers.forEach(player => {
        items.push({
          type: 'player',
          icon: '⚽',
          title: player.name,
          subtitle: `${player.position} · ${player.currentClub}`,
          action: () => navigate(`/players/${player._id}`)
        });
      });
    }

    items.push({ type: 'section', title: 'Quick Actions' });
    quickActions.forEach(action => {
      items.push({
        type: 'action',
        ...action
      });
    });

    return items;
  }, [query, searchResults, recentPlayers, loading, quickActions, navigate]);

  const items = getAllItems();
  const selectableItems = items.filter(i => i.action);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(i => Math.min(i + 1, selectableItems.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(i => Math.max(i - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (selectableItems[selectedIndex]?.action) {
            selectableItems[selectedIndex].action();
            onClose();
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, selectableItems, onClose]);

  // Reset selected index when items change
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  if (!isOpen) return null;

  let selectableIndex = -1;

  return (
    <div className="command-palette-overlay" onClick={onClose}>
      <div className="command-palette" onClick={e => e.stopPropagation()}>
        <div className="command-palette-input-wrapper">
          <span className="command-palette-input-icon">🔍</span>
          <input
            ref={inputRef}
            type="text"
            className="command-palette-input"
            placeholder="Search players, reports, commands..."
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          <kbd className="command-palette-kbd">ESC</kbd>
        </div>

        <div className="command-palette-list">
          {items.map((item, index) => {
            if (item.type === 'section') {
              return (
                <div key={index} className="command-palette-section">
                  <div className="command-palette-section-title">{item.title}</div>
                </div>
              );
            }

            if (item.type === 'loading' || item.type === 'empty') {
              return (
                <div 
                  key={index} 
                  className="command-palette-item"
                  style={{ opacity: 0.5, cursor: 'default' }}
                >
                  <div className="command-palette-item-content">
                    <div className="command-palette-item-title">{item.title}</div>
                  </div>
                </div>
              );
            }

            if (item.action) {
              selectableIndex++;
              const isSelected = selectableIndex === selectedIndex;

              return (
                <div
                  key={index}
                  className={`command-palette-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => {
                    item.action();
                    onClose();
                  }}
                  onMouseEnter={() => setSelectedIndex(selectableIndex)}
                >
                  <div className="command-palette-item-icon">{item.icon}</div>
                  <div className="command-palette-item-content">
                    <div className="command-palette-item-title">{item.title}</div>
                    {item.subtitle && (
                      <div className="command-palette-item-subtitle">{item.subtitle}</div>
                    )}
                  </div>
                  {item.shortcut && (
                    <div className="command-palette-item-shortcut">
                      <kbd className="command-palette-kbd">{item.shortcut}</kbd>
                    </div>
                  )}
                </div>
              );
            }

            return null;
          })}
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
